import React from 'react';
import { Shield, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";

const PERMISSIONS = [
  { key: 'can_create_challenges', label: 'Create Challenges', desc: 'Can create and manage writing challenges' },
  { key: 'can_judge_challenges', label: 'Judge Challenges', desc: 'Can score and judge challenge entries' },
  { key: 'can_manage_flags', label: 'Manage Flags', desc: 'Can view and resolve reported content' },
  { key: 'can_warn_users', label: 'Warn Users', desc: 'Can send warnings to users' },
  { key: 'can_suspend_users', label: 'Suspend Users', desc: 'Can temporarily suspend user accounts' },
];

export const AdminModPermsTab = ({ users, selectedMod, modPermissions, savingPerms, handleSelectMod, setModPermissions, handleSaveModPermissions }) => {
  const moderators = users.filter(u => u.role === 'moderator');

  return (
    <Card className="border-brand-primary/10">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shield className="w-5 h-5 text-brand-accent" /> Moderator Permissions
        </CardTitle>
        <p className="text-sm text-text-muted mt-1">Select a moderator and toggle their specific permissions</p>
      </CardHeader>
      <CardContent>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <Label className="mb-3 block font-medium">Moderators</Label>
            <div className="space-y-2">
              {moderators.length === 0 ? (
                <p className="text-sm text-text-muted py-4 text-center">No moderators assigned. Promote a user first.</p>
              ) : moderators.map(mod => (
                <button key={mod.id} onClick={() => handleSelectMod(mod)} data-testid={`select-mod-${mod.id}`}
                  className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors text-left ${selectedMod?.id === mod.id ? 'bg-brand-accent/10 border border-brand-accent/30' : 'bg-surface-alt hover:bg-brand-primary/5'}`}>
                  <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center text-purple-700 font-medium text-sm">
                    {(mod.display_name || mod.username)?.[0]?.toUpperCase()}
                  </div>
                  <div>
                    <p className="font-medium text-text-primary text-sm">{mod.display_name || mod.username}</p>
                    <p className="text-xs text-text-muted">@{mod.username}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label className="mb-3 block font-medium">Permissions {selectedMod && `for ${selectedMod.display_name || selectedMod.username}`}</Label>
            {!selectedMod ? (
              <p className="text-sm text-text-muted py-4 text-center">Select a moderator to manage permissions</p>
            ) : (
              <div className="space-y-3">
                {PERMISSIONS.map(perm => (
                  <div key={perm.key} className="flex items-center justify-between p-3 bg-surface-alt rounded-lg" data-testid={`perm-toggle-${perm.key}`}>
                    <div>
                      <p className="font-medium text-text-primary text-sm">{perm.label}</p>
                      <p className="text-xs text-text-muted">{perm.desc}</p>
                    </div>
                    <button onClick={() => setModPermissions(prev => ({ ...prev, [perm.key]: !prev[perm.key] }))} data-testid={`toggle-${perm.key}`}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${modPermissions[perm.key] ? 'bg-brand-accent' : 'bg-gray-300'}`}>
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${modPermissions[perm.key] ? 'translate-x-6' : 'translate-x-1'}`} />
                    </button>
                  </div>
                ))}
                <Button className="btn-primary w-full mt-4" onClick={handleSaveModPermissions} disabled={savingPerms} data-testid="save-mod-permissions-btn">
                  {savingPerms ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Save Permissions
                </Button>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
